// src/components/widgets/WidgetVersionListItem.jsx (New File)
import React from 'react';
import {
  TableRow, TableCell, Stack, Avatar, Typography, Box, Chip, Tooltip, IconButton, Link as MuiLink, useTheme, alpha, CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Code as EmbedIcon,
  CheckCircle as ActivateIcon,
  Star as StarIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Helper function (can be moved to a utils file if used elsewhere)
const getAvatarContent = (name) => {
  if (!name) return 'V';
  const words = name.split(' ');
  if (words.length > 1 && words[0] && words[words.length - 1]) {
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  }
  if (name.length > 1) {
    return name.substring(0, 2).toUpperCase();
  }
  if (name.length === 1) {
    return name.toUpperCase();
  }
  return 'V';
};

const WidgetVersionListItem = ({
  version,
  onActivate,    // Func to call when activate is clicked: (versionId) => void
  onDelete,      // Func to call when delete is clicked: (versionId) => void
  onOpenEmbedModal, // Func to call to open embed modal: (versionObject) => void
  isActivating,  // Boolean, true if this version is currently being activated
  isLoadingActions, // Boolean, true if a general action is happening (e.g. main list loading)
  showTemplateName = false, // Prop to control if template name column is shown
  showDescription = false,  // Prop to control if description column is shown
  showUpdatedAt = false,    // Prop to control if updated_at column is shown
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const v = version; // Alias for brevity

  const handleCopyId = () => {
    navigator.clipboard.writeText(v.id)
      .then(() => toast.success('Version ID copied!'))
      .catch(() => toast.error('Failed to copy ID.'));
  };

  return (
    <TableRow
      key={v.id}
      hover
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        backgroundColor: v.is_active ? alpha(theme.palette.success.light, 0.08) : 'inherit',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        }
      }}
    >
      {/* NAME & ID CELL */}
      <TableCell sx={{ py: 1.25, width: showTemplateName || showDescription ? 'auto' : '40%' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              bgcolor: v.is_active ? theme.palette.success.main : theme.palette.primary.main,
              color: 'white',
              width: 38,
              height: 38,
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            {getAvatarContent(v.version_name || v.name)}
          </Avatar>
          <Box>
            <MuiLink
              component={RouterLink}
              to={`/widgets/versions/${v.id}/edit`}
              underline="hover"
              fontWeight="500" // Medium weight for better readability
              color="text.primary"
              sx={{
                fontSize: '0.9375rem', // Slightly smaller than h6 but prominent
                display: 'block',
                mb: 0.25,
                '&:hover': { color: 'primary.main' }
              }}
            >
              {v.version_name || v.name || "Unnamed Version"}
            </MuiLink>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                ID: {v.id.substring(0, 8)}...{v.id.slice(-4)}
              </Typography>
              <Tooltip title={`Copy full Version ID: ${v.id}`}>
                <IconButton
                  onClick={handleCopyId}
                  size="small" sx={{ p: '2px', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  <CopyIcon sx={{ fontSize: '0.8rem' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Stack>
      </TableCell>

      {/* TEMPLATE NAME CELL (Conditional) */}
      {showTemplateName && (
        <TableCell sx={{ py: 1.25 }}>
          {v.template ? (
            <MuiLink component={RouterLink} to={`/widgets/${v.template.id}/versions`} underline="hover" sx={{fontSize: '0.875rem'}}>
              {v.template.name || "N/A"}
            </MuiLink>
          ) : (
            <Typography variant="caption" color="text.secondary">N/A</Typography>
          )}
        </TableCell>
      )}
      
      {/* DESCRIPTION CELL (Conditional) */}
      {showDescription && (
         <TableCell sx={{ py: 1.25, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem', color: 'text.secondary' }}>
            {v.description || <Typography variant="caption" color="text.secondary">No description</Typography>}
        </TableCell>
      )}

      {/* STATUS CELL */}
      <TableCell sx={{ py: 1.25 }}>
        {v.is_active ? (
          <Chip
            label="Active"
            color="success"
            size="small"
            icon={<StarIcon sx={{ fontSize: '1rem !important', ml: '6px' }} />} // icon on right
            variant="filled"
            sx={{ fontWeight: 500, borderRadius: '6px' }}
          />
        ) : (
          <Chip
            label="Inactive"
            color="default"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 400, borderRadius: '6px', color: 'text.disabled', borderColor: theme.palette.divider }}
          />
        )}
      </TableCell>

      {/* UPDATED AT CELL (Conditional) */}
      {showUpdatedAt && (
        <TableCell sx={{ py: 1.25, fontSize: '0.8125rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {new Date(v.updated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric'})}
            <Typography variant="caption" display="block" sx={{fontSize: '0.7rem'}}>
                {new Date(v.updated_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit'})}
            </Typography>
        </TableCell>
      )}

      {/* ACTIONS CELL */}
      <TableCell align="right" sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
        <Tooltip title="Edit Version">
          <span>
            <IconButton size="small" onClick={() => navigate(`/widgets/versions/${v.id}/edit`)} disabled={isLoadingActions}>
              <EditIcon fontSize="small"/>
            </IconButton>
          </span>
        </Tooltip>
        {!v.is_active && (
          <Tooltip title="Set as Active Version">
            <span>
              <IconButton size="small" onClick={() => onActivate && onActivate(v.id)} disabled={isActivating || isLoadingActions}>
                {isActivating ? <CircularProgress size={18} color="inherit"/> : <ActivateIcon fontSize="small"/>}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Get Embed Code">
          <span>
            <IconButton size="small" onClick={() => onOpenEmbedModal && onOpenEmbedModal(v)} disabled={isLoadingActions}>
              <EmbedIcon fontSize="small"/>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete Version">
          <span>
            <IconButton size="small" color="error" onClick={() => onDelete && onDelete(v.id)} disabled={isLoadingActions}>
              <DeleteIcon fontSize="small"/>
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default WidgetVersionListItem;